import { createFileRoute } from "@tanstack/react-router";
import { entityConfigMap } from "lib/components/procure/entityConfigs";
import { TYPE_EMPTY_KAM_MAPPING } from "procure/takeoffs/measureTypeMaps";
import { useState } from "react";
import Input from "ui/forms/Input";
import withAuthentication from "../../../components/withAuthentication";
import styles from "./bapi-kams.module.scss";

function BapiKamsPage() {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();

  const allEntries = Object.entries(TYPE_EMPTY_KAM_MAPPING).map(([kamType, kam]) => {
    const title = entityConfigMap[kamType as keyof typeof entityConfigMap]?.title ?? kamType;
    return { kamType, kam, title };
  });

  const filtered = allEntries.filter(({ kamType, title }) => {
    if (!query) {
      return true;
    }
    const attrKeys = Object.keys(TYPE_EMPTY_KAM_MAPPING[kamType as keyof typeof TYPE_EMPTY_KAM_MAPPING].attributes);
    const measureKeys = Object.keys(TYPE_EMPTY_KAM_MAPPING[kamType as keyof typeof TYPE_EMPTY_KAM_MAPPING].measures);
    return (
      kamType.toLowerCase().includes(query) ||
      title.toLowerCase().includes(query) ||
      attrKeys.some((k) => k.toLowerCase().includes(query)) ||
      measureKeys.some((k) => k.toLowerCase().includes(query))
    );
  });

  const sortedEntries = [...filtered].sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>BAPI KAM Schema Viewer</h1>
        <p>
          Attributes and measures produced by each KAM category, sourced directly from{" "}
          <code>packages/procure/takeoffs/measureTypeMaps.ts</code>. For raw controller schemas see{" "}
          <a href="/debug/schema/building-api">/debug/schema/building-api</a>.
        </p>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.stats}>
          <span className={styles.stat}>
            <strong>{allEntries.length}</strong> KAM types
          </span>
          {query && (
            <span className={styles.stat}>
              <strong>{filtered.length}</strong> matching
            </span>
          )}
        </div>
        <Input
          inputSize="medium"
          type="search"
          placeholder="Search types, fields…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ width: 280 }}
        />
      </div>

      {sortedEntries.length === 0 && (
        <div className={styles.emptyState}>No KAM types match &ldquo;{search}&rdquo;.</div>
      )}

      <div className={styles.groupGrid}>
        {sortedEntries.map(({ kamType, kam, title }) => {
          const attributes = Object.entries(kam.attributes);
          const measures = Object.entries(kam.measures);
          const measureUnitByName = new Map(measures.map(([name, m]) => [name, m.unit]));
          return (
            <div key={kamType} className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.kamTitle}>{title}</span>
                <span className={styles.kamType}>{kamType}</span>
              </div>
              <div className={styles.cardBody}>
                <FieldSection
                  title="Attributes"
                  rows={attributes.map(([name, a]) => ({
                    name,
                    unit: a.unit ?? measureUnitByName.get(name) ?? "string",
                  }))}
                />
                <FieldSection title="Measures" rows={measures.map(([name, m]) => ({ name, unit: m.unit }))} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FieldSection({ title, rows }: { title: string; rows: { name: string; unit: string }[] }) {
  return (
    <div className={styles.fieldSection}>
      <div className={styles.fieldSectionTitle}>
        {title}
        <span className={styles.fieldCount}>{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <span className={styles.emptyField}>none</span>
      ) : (
        <ul className={styles.fieldList}>
          {rows.map(({ name, unit }) => (
            <li key={name} className={styles.fieldRow}>
              <span className={styles.fieldName}>{name}</span>
              <span className={styles.fieldUnit}>{unit}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/debug/schema/bapi-kams")({
  component: withAuthentication(BapiKamsPage, { superAdmin: true }),
});
